import { observer } from 'mobx-react'
import type { CSSProperties } from 'react'
import { useContext, useEffect, useRef, useState } from 'react'
import { Button, Header, LoadingSpinner } from '@/components/Generic'
import { StoreContext } from '@store/Root.store'
import { axiosInstance } from '@/methods/axiosConfig'
import { API_URL, EID_SERVICE_PATH } from '@/constants/apiUrl'
import { devLog } from '@/methods/devLog'
import { forceRedirect } from '@/methods/forceRedirect'
import { cx } from '@/utils/cx'

interface GetContextResponse {
  isRetryAttempt: boolean
  expectedData: {
    firstName: string | undefined
    lastName: string | undefined
  }
  actualData: {
    firstName: string | undefined
    lastName: string | undefined
  }
}

interface GenerateProceedUrlResponse {
  proceedUrl: string
}

export const EidPage = observer(() => {
  const buttonRef = useRef(null)

  const store = useContext(StoreContext)
  const {
    RetryElavonPage: { eid: trans },
  } = store.TranslationsState.translations
  const { screenSize, isMobile } = store.AppState

  const [btnLoading, setBtnLoading] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [expectedName, setExpectedName] = useState('')
  const [actualName, setActualName] = useState('')

  // Mobile utilities
  const [mobileContainerHeight, setMobileContainerHeight] = useState('')

  const generateProceedUrl = async () => {
    try {
      const { data } = await axiosInstance.post<GenerateProceedUrlResponse>(
        `${API_URL}/${EID_SERVICE_PATH}/generate-proceed-url`,
        {},
        {
          withCredentials: true,
        }
      )

      data?.proceedUrl && forceRedirect(data.proceedUrl)
    } catch (error) {
      devLog('Error generating url:', error)
    }
  }

  const getContext = async () => {
    try {
      const { data } = await axiosInstance.get<GetContextResponse>(
        `${API_URL}/${EID_SERVICE_PATH}/get-context`,
        {
          withCredentials: true,
        }
      )

      if (!data?.isRetryAttempt) {
        generateProceedUrl()
      } else {
        setExpectedName(`${data?.expectedData.firstName} ${data?.expectedData.lastName}`)
        data?.actualData &&
          setActualName(`${data?.actualData.firstName} ${data?.actualData.lastName}`)
        setIsLoading(false)
      }
    } catch (error) {
      devLog('Error fetching context:', error)
    }
  }

  const handleProceed = () => {
    setBtnLoading(true)
    generateProceedUrl()
  }

  useEffect(() => {
    if (screenSize.width && screenSize.width <= 980) {
      setMobileContainerHeight(`calc(${window.innerHeight}px - 120px)`)
    }
  }, [screenSize.width])

  useEffect(() => {
    void getContext()
  }, [])

  if (isLoading) {
    return (
      <div className="page-container mobile-m-10">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="page-container mobile-m-0">
      <div
        className="layout__content-container-with-scroll"
        style={
          isMobile
            ? ({ '--ccws-mobile-height': mobileContainerHeight } as CSSProperties)
            : undefined
        }
      >
        <Header className="no-margin">{trans.header}</Header>

        <p className="generic__paragraph">{trans.description}</p>

        <div className="generic__spacer h-4" />

        <p className="generic__paragraph">{trans.expected}</p>

        <div className="elavon__name-wrapper">{expectedName}</div>

        <div className="generic__spacer h-4" />

        <p className="generic__paragraph">{trans.actual}</p>

        <div className="elavon__name-wrapper">{actualName}</div>

        {isMobile ? (
          <div className="layout__margined m-0-0-40" />
        ) : (
          <div className="layout__margined m-0-0-15" />
        )}
      </div>

      <div className="layout__content-container-bottom-fixed-centered">
        <div className="layout__vertically-centered-container">
          <Button onClick={handleProceed} tabIndex={0} className="button--proceed" ref={buttonRef}>
            {btnLoading && <LoadingSpinner inButton />}
            <span
              className={cx('generic__button-text', btnLoading && 'generic__button-text--loading')}
              data-testId="proceed"
            >
              {trans.tryAgain}
            </span>
          </Button>
        </div>
      </div>
    </div>
  )
})
